// Worker compile helpers shared by runtime compile dispatch.
// They isolate warm-up, feature-load timing, and worker result normalization.
export type { LuisWorkerDispatchResult } from './types'
import type { LuisWorkerDispatchResult } from './types'
import { buildContextPrelude } from '../loader/context-serialize'
import { getNow } from '../loader/context-time'
import { ensureRuntimeFeatures, getRuntimeFeature } from '../features'
import { getScheduledWorkerWarmupPromise } from '../loader/process/worker-warmup'
import type { BuildSourceEntry } from '../loader/process/types'
import type { LuisRuntimeTimingBreakdown, LuisRuntimeWorkerState } from '../features'
import type { LuisCompileContext } from '../loader/context'

// Compile one source through worker path with feature-load timing details.
export async function compileOnWorker(
  entry: BuildSourceEntry,
  context: LuisCompileContext,
  debugEnabled: boolean,
): Promise<LuisWorkerDispatchResult | null> {
  const warmupPromise = getScheduledWorkerWarmupPromise()
  // Reuse in-flight idle warm-up before forcing a direct worker feature load.
  if (warmupPromise) {
    await warmupPromise
  } else {
    // Continue direct worker feature load when no idle warm-up is active.
  }

  const featureLoadStart = getNow()
  await ensureRuntimeFeatures(new Set(['worker']))
  const featureLoadDurationMs = getNow() - featureLoadStart
  const workerFeature = getRuntimeFeature('worker')
  // Check whether worker feature was loaded successfully.
  if (!workerFeature) {
    // Return null when worker feature could not be loaded.
    return null
  } else {
    // Continue external worker compile through loaded worker host API.
  }

  const contextPrelude = buildContextPrelude(context)
  const workerCompile = await workerFeature.compileSource({
    source: entry.rawSource,
    sourceId: entry.kind === 'link' ? entry.href : entry.sourceId,
    contextPrelude,
    lineOffset: countPreludeLines(contextPrelude),
    compileMode: entry.compileModeOverride,
    debugEnabled,
    serializedImportGraph: entry.serializedImportGraph,
    serializedImportModes: entry.serializedImportModes,
  })
  context.classVariables = { ...workerCompile.result.classVariables }
  context.objectVariables = { ...workerCompile.result.objectVariables }
  context.arrayVariables = { ...workerCompile.result.arrayVariables }

  // Return worker compile payload with execution metadata.
  return {
    result: workerCompile.result,
    compileDurationMs: workerCompile.compileDurationMs,
    incremental: workerCompile.incremental,
    execution: 'worker',
    workerState: workerCompile.workerState,
    timing: {
      ...workerCompile.timing,
      featureLoadDurationMs: featureLoadDurationMs > 0 ? featureLoadDurationMs : undefined,
    },
  }
}

// Count prelude lines so debug source lines map back to original source.
function countPreludeLines(prelude: string): number {
  // Check whether prelude text is empty before counting lines.
  if (!prelude) {
    // Return zero offset when no context prelude was prepended.
    return 0
  } else {
    // Continue source-line offset calculation.
  }

  // Return line count for prepended prelude text.
  return prelude.split('\n').length
}
